import { Controller } from "@hotwired/stimulus"

// Lightweight, dependency-free tooltip. Renders into <body> (so it is never
// clipped by overflow or pinned headers) and flips above/clamps horizontally
// when it would run off-screen. Reads its text from data-tooltip-text-value,
// else the element's title/aria-label — and strips the native title so the
// browser tooltip never double-renders.
//
// Usage: data-controller="tooltip" data-tooltip-text-value="Show filters"
// Use data-tooltip-only-overflow-value="true" on truncated labels so the
// tooltip only appears when the rendered text is actually clipped.
// Use data-tooltip-enabled-by-value="--some-flag" to let a stylesheet decide:
// the tooltip only appears while that custom property computes to 1 on the
// element (the collapsed sidebar rail names its icons this way).
// data-tooltip-placement-value="right" sets the tip beside the element instead
// of below it, falling back to below when there is no room on the right.
export default class extends Controller {
  static values = {
    text: String,
    onlyOverflow: { type: Boolean, default: false },
    enabledBy: String,
    placement: { type: String, default: "bottom" }
  }

  connect() {
    this.text = this.hasTextValue && this.textValue.length
      ? this.textValue
      : (this.element.getAttribute("data-tip")
        || this.element.getAttribute("title")
        || this.element.getAttribute("aria-label")
        || "")

    // Strip the native title (and any inner title) so the browser tooltip
    // can't double-render alongside ours.
    if (this.element.hasAttribute("title")) this.element.removeAttribute("title")
    this.element.querySelectorAll("[title]").forEach((el) => el.removeAttribute("title"))

    this.boundShow = this.show.bind(this)
    this.boundHide = this.hide.bind(this)
    this.element.addEventListener("mouseenter", this.boundShow)
    this.element.addEventListener("mouseleave", this.boundHide)
    this.element.addEventListener("focusin", this.boundShow)
    this.element.addEventListener("focusout", this.boundHide)
    this.element.addEventListener("click", this.boundHide)
  }

  disconnect() {
    this.hide()
    this.element.removeEventListener("mouseenter", this.boundShow)
    this.element.removeEventListener("mouseleave", this.boundHide)
    this.element.removeEventListener("focusin", this.boundShow)
    this.element.removeEventListener("focusout", this.boundHide)
    this.element.removeEventListener("click", this.boundHide)
  }

  show() {
    if (this.onlyOverflowValue && !this.isOverflowing()) return
    if (this.enabledByValue && getComputedStyle(this.element).getPropertyValue(this.enabledByValue).trim() !== "1") return
    if (!this.text || this.tip) return
    this.tip = document.createElement("div")
    this.tip.className = "app-tooltip"
    this.tip.setAttribute("role", "tooltip")
    this.tip.textContent = this.text
    document.body.appendChild(this.tip)
    this.position()
    requestAnimationFrame(() => this.tip && this.tip.classList.add("app-tooltip--visible"))
  }

  position() {
    const anchor = this.element.getBoundingClientRect()
    const tip = this.tip.getBoundingClientRect()
    const gap = 6
    if (this.placementValue === "right" && anchor.right + gap + tip.width <= window.innerWidth - 4) {
      const top = anchor.top + anchor.height / 2 - tip.height / 2
      this.tip.style.top = `${Math.round(Math.max(4, Math.min(top, window.innerHeight - tip.height - 4)))}px`
      this.tip.style.left = `${Math.round(anchor.right + gap)}px`
      return
    }
    let top = anchor.bottom + gap
    if (top + tip.height > window.innerHeight - 4) {
      top = anchor.top - tip.height - gap
    }
    let left = anchor.left + anchor.width / 2 - tip.width / 2
    left = Math.max(4, Math.min(left, window.innerWidth - tip.width - 4))
    this.tip.style.top = `${Math.round(top)}px`
    this.tip.style.left = `${Math.round(left)}px`
  }

  hide() {
    if (this.tip) {
      this.tip.remove()
      this.tip = null
    }
  }

  isOverflowing() {
    return this.element.scrollWidth > this.element.clientWidth || this.element.scrollHeight > this.element.clientHeight
  }
}
